import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface Group {
  id: string;
  name: string;
  level: string;
  day: string;
  time: string;
  whatsapp_link: string;
  color: string;
  active: boolean;
}

export interface Lesson {
  id: string;
  title: string;
  group_id: string;
  level: string;
  subject: string;
  date: string;
  start_time: string;
  end_time: string;
  type: string;
  live_link: string;
  description: string;
  status: string;
  has_record: boolean;
  has_document: boolean;
  has_assignment: boolean;
}

export interface LessonRecord {
  id: string;
  title: string;
  lesson_id: string;
  group_id: string;
  description: string;
  video_url: string;
  alt_url: string;
  published: boolean;
  visible_to_students: boolean;
  date: string;
  views: number;
  duration: string;
}

export interface Assignment {
  id: string;
  title: string;
  description: string;
  lesson_id: string;
  group_id: string;
  deadline: string;
  sent_date: string;
  status: string;
  required: boolean;
  reminder: boolean;
  has_file: boolean;
  link: string;
  file_url: string;
  target_type: string;
  target_students: string[] | null;
}

export interface DocumentItem {
  id: string;
  title: string;
  description: string;
  type: string;
  lesson_id: string;
  group_id: string;
  level: string;
  published: boolean;
  downloadable: boolean;
  upload_date: string;
  downloads: number;
  size: string;
  file_url: string;
}

export interface Announcement {
  id: string;
  title: string;
  message: string;
  group_id: string;
  date: string;
  priority: string;
  visible: boolean;
  views: number;
}

export interface DashboardData {
  groups: Group[];
  lessons: Lesson[];
  records: LessonRecord[];
  assignments: Assignment[];
  documents: DocumentItem[];
  announcements: Announcement[];
  activeStudents: number;
  passiveStudents: number;
  totalStudents: number;
  loading: boolean;
  error: string | null;
}

export function useDashboardData(): DashboardData {
  const [data, setData] = useState<DashboardData>({
    groups: [],
    lessons: [],
    records: [],
    assignments: [],
    documents: [],
    announcements: [],
    activeStudents: 0,
    passiveStudents: 0,
    totalStudents: 0,
    loading: true,
    error: null,
  });

  const fetchAll = useCallback(async () => {
    setData((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [
        { data: groups, error: groupsError },
        { data: lessons, error: lessonsError },
        { data: records, error: recordsError },
        { data: assignments, error: assignmentsError },
        { data: documents, error: documentsError },
        { data: announcements, error: announcementsError },
        { data: profiles, error: profilesError },
      ] = await Promise.all([
        supabase.from('zehrahoca_groups').select('*').eq('active', true).order('created_at', { ascending: true }),
        supabase.from('zehrahoca_lessons').select('*').order('date', { ascending: true }),
        supabase.from('zehrahoca_lesson_records').select('*').order('date', { ascending: false }),
        supabase.from('zehrahoca_assignments').select('*').order('sent_date', { ascending: false }),
        supabase.from('zehrahoca_documents').select('*').order('upload_date', { ascending: false }),
        supabase.from('zehrahoca_announcements').select('*').eq('visible', true).order('date', { ascending: false }),
        supabase.from('zehrahoca_profiles').select('active').eq('role', 'ogrenci'),
      ]);

      if (groupsError) throw groupsError;
      if (lessonsError) throw lessonsError;
      if (recordsError) throw recordsError;
      if (assignmentsError) throw assignmentsError;
      if (documentsError) throw documentsError;
      if (announcementsError) throw announcementsError;
      if (profilesError) throw profilesError;

      const profileList = (profiles ?? []) as { active: boolean }[];
      const activeStudents = profileList.filter((p) => p.active).length;
      const passiveStudents = profileList.filter((p) => !p.active).length;

      setData({
        groups: (groups ?? []) as Group[],
        lessons: (lessons ?? []) as Lesson[],
        records: (records ?? []) as LessonRecord[],
        assignments: (assignments ?? []) as Assignment[],
        documents: (documents ?? []) as DocumentItem[],
        announcements: (announcements ?? []) as Announcement[],
        activeStudents,
        passiveStudents,
        totalStudents: activeStudents + passiveStudents,
        loading: false,
        error: null,
      });
    } catch (err) {
      setData((prev) => ({ ...prev, loading: false, error: (err as Error).message }));
    }
  }, []);

  useEffect(() => {
    fetchAll();

    const handleChange = () => {
      fetchAll();
    };
    window.addEventListener('zehrahoca:lessons-changed', handleChange);
    return () => {
      window.removeEventListener('zehrahoca:lessons-changed', handleChange);
    };
  }, [fetchAll]);

  return data;
}